import uuid from "react-uuid";
import { AppState, Action, ChatHistory, OrchestrationState, UploadingStatus, getCurrentOrchestration } from "../common/models";

export function appStateReducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SET_CURRENT_AGENT':
      return {
        ...state,
        currentAgent: action.payload,
      };
    case 'SET_ORCHESTRATION_STATE':

    // Update currentAgent based on the new orchestration state
      const currentOrch = getCurrentOrchestration(action.payload);

      return {
        ...state,
        currentOrchState: action.payload,
        currentAgent: currentOrch?.currentAgent ?? state.currentAgent,
      };
    case 'UPDATE_CHAT_MESSAGE':
      // find message in chat and delete every index after it
      const messageIndex = state.currentConversation.messages.findIndex((message) => message.messageId === action.payload.messageId);
      const messages = state.currentConversation.messages.slice(0, messageIndex);
      const updatedMessages = [...messages, action.payload];

      return {
        ...state,
        currentConversation: {
          ...state.currentConversation,
          messages: updatedMessages,
        },
      };
    case "ADD_CHAT_MESSAGE":
      let isNewMessage = true;

      let newMessages = state.currentConversation.messages.map((message) => {
        if (message.messageId === action.payload.messageId) {
          isNewMessage = false;
          return { 
            ...message, 
            content: action.payload.content, 
            authorRole: action.payload.authorRole,
            timestamp: action.payload.timestamp,
            agentName: action.payload.agentName // ✅ Make sure to include agentName when updating
          };
        }
        return message;
      });

      return {
        ...state,
        currentConversation: {
          sessionId: state.currentConversation.sessionId,
          messages: isNewMessage
            ? [...newMessages, action.payload]
            : newMessages,
          attachments: state.currentConversation.attachments,
        },
      };
    case "REPLACE_MESSAGE_ID":
      return {
        ...state,
        currentConversation: {
          sessionId: state.currentConversation.sessionId,
          messages: state.currentConversation.messages.map((message) => {
            if (message.messageId === action.payload.oldId) {
              return { ...message, messageId: action.payload.newId };
            }

            return message;
          }),
          attachments: state.currentConversation.attachments,
        },
      };
    case "CLEAR_CURRENT_CHAT":
      return {
        ...state,
        currentConversation: { sessionId: null, messages: [], attachments: [] },
        currentAgent: "NegotiatorAgent",
        currentOrchState: null,
        // Also reset any uploading status
        uploadingStatus: UploadingStatus.STANDBY,
        isReadOnlyMode: false, // Reset read-only mode on new chat
      };
    case 'SET_READ_ONLY_MODE':
      return {
        ...state,
        isReadOnlyMode: action.payload,
      };
    case 'REMOVE_SESSION_FROM_HISTORY':
      let shallowCopyOfChatHistory = state.chatHistory?.chatHistory || {};
      delete shallowCopyOfChatHistory[action.payload];

      let shallowCopyOfAttachments = state.chatHistory?.attachments || {};
      delete shallowCopyOfAttachments[action.payload];

      return {
        ...state,
        chatHistory: {
          chatHistory: shallowCopyOfChatHistory,
          attachments: shallowCopyOfAttachments
        },
        currentConversation: state.currentConversation.sessionId === action.payload ? { sessionId: uuid(), messages: [], attachments: [] } : state.currentConversation
      }
    case "SET_CHAT_HISTORY_LOADING":
      return {
        ...state,
        isChatHistoryLoading: action.payload,
      };
    case "SET_CHAT_HISTORY":
      return {
        ...state,
        chatHistory: action.payload,
      };
    case "SET_IS_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      };
    case "TOGGLE_CHAT_HISTORY":
      return {
        ...state,
        isChatHistoryOpen: !state.isChatHistoryOpen,
      };
    case "SET_CURRENT_CONVERSATION":
      const sessionId = action.payload.sessionId ?? state.currentConversation.sessionId ?? uuid();
      // If the payload already has attachments, use those; otherwise fall back to chat history
      const attachments = action.payload.attachments || state.chatHistory?.attachments?.[action.payload.sessionId ?? ''] || [];
      return {
        ...state,
        currentConversation: {
          ...action.payload,
          sessionId,
          attachments,
        },
        activeDocument: attachments[0] || null
      };
    case "SET_SESSION_ID":
      return {
        ...state,
        currentConversation: {
          ...state.currentConversation,
          sessionId: action.payload,
        },
      };
    case "SET_ACTIVE_DOCUMENT":
      return {
        ...state,
        activeDocument: action.payload
      }
    case "SET_UPLOADING_STATUS":
      return {
        ...state,
        uploadingStatus: action.payload
      }
    case 'SET_IS_PHONE':
      return {
        ...state,
        isPhone: action.payload
      }
    case 'SET_IS_CHROME_EXTENSION':
      return {
        ...state,
        isChromeExtension: action.payload
      }
    case "SET_PROMPTS":
      return {
        ...state,
        prompts: action.payload,
      }
    case "ADD_PROMPT":
      return {
        ...state,
        prompts: [...state.prompts, action.payload],
      }
    case 'SET_THEME_MODE':
      return {
        ...state,
        themeMode: action.payload
      }  
    case 'CLEAR_ORCHSTATE_SUGGESTIONS':

      return {
        ...state,
        currentOrchState: {
          ...state.currentOrchState,
          nextPrompt: "",
          topicIndicesByAgent: state.currentOrchState?.topicIndicesByAgent ?? {},
          completedAgents: state.currentOrchState?.completedAgents ?? [],
          conversationFlags: state.currentOrchState?.conversationFlags ?? { backtrackRequested: false, userConfused: false, agentRecommendationPending: false },
          userIntent: state.currentOrchState?.userIntent ?? { type: "", targetAgent: "", confidence: 0 },
          topicCoverage: state.currentOrchState?.topicCoverage ?? {},
          completionConfidenceByAgent: state.currentOrchState?.completionConfidenceByAgent ?? {},
          orchestrations: state.currentOrchState?.orchestrations ?? []
        },
      };
    case 'SET_SHOULD_ADD_CONTINUE_OPTION':
      return {
        ...state,
        shouldAddContinueOption: action.payload,
      };
    case 'SET_MAX_STEP':
      return {
        ...state,
        maxStep: action.payload,
      };
    case 'SET_CURRENT_ORCHESTRATION_TYPE':
      return {
        ...state,
        currentOrchestrationType: action.payload,
        ...(state.currentOrchState && {
          currentOrchState: {
            ...state.currentOrchState,
            currentOrchestrationType: action.payload,
          },
        }),
      };
    default:
      return state;
  }
}
